import {
  doc,
  getDoc,
  query,
  where,
  getDocs,
  collection
} from 'firebase/firestore'
import { db } from '../lib/firebase'
import type {
  UserLoyalty,
  ActiveBonus
} from '../types'

class LoyaltyService {

  /**
   * Get user loyalty data (READ ONLY)
   */
  async getUserLoyalty(userId: string): Promise<UserLoyalty | null> {
    try {
      const loyaltyDoc = await getDoc(doc(db, 'loyalty', userId))
      if (!loyaltyDoc.exists()) {
        return null
      }

      const data = loyaltyDoc.data()
      return {
        ...data,
        joinedAt: data.joinedAt?.toDate() || new Date(),
        achievements: data.achievements?.map((achievement: any) => ({
          ...achievement,
          unlockedAt: achievement.unlockedAt?.toDate()
        })) || [],
        pointsHistory: data.pointsHistory?.map((transaction: any) => ({
          ...transaction,
          createdAt: transaction.createdAt?.toDate() || new Date()
        })) || []
      } as UserLoyalty
    } catch (error) {
      console.error('Error getting user loyalty:', error)
      return null
    }
  }

  /**
   * Get active bonus for a raffle (READ ONLY)
   */
  async getActiveBonus(raffleId: string): Promise<ActiveBonus | null> {
    try {
      const bonusDoc = await getDoc(doc(db, 'activeBonuses', raffleId))
      if (!bonusDoc.exists()) {
        return null
      }

      const data = bonusDoc.data()
      if (data.removed) {
        return null
      }

      // Check if bonus has expired
      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        return null
      }

      return {
        raffleId: data.raffleId,
        bonusPercentage: data.bonusPercentage,
        reason: data.reason,
        appliedAt: data.appliedAt.toDate(),
        expiresAt: data.expiresAt?.toDate()
      }
    } catch (error) {
      console.error('Error getting active bonus:', error)
      return null
    }
  }

  /**
   * Get all active bonuses (READ ONLY)
   */
  async getActiveBonuses(): Promise<ActiveBonus[]> {
    try {
      const bonusesQuery = query(
        collection(db, 'activeBonuses'),
        where('removed', '!=', true)
      )
      const snapshot = await getDocs(bonusesQuery)

      const bonuses: ActiveBonus[] = []
      const now = new Date()

      snapshot.docs.forEach(doc => {
        const data = doc.data()

        // Skip expired bonuses
        if (data.expiresAt && data.expiresAt.toDate() < now) {
          return
        }

        bonuses.push({
          raffleId: data.raffleId,
          bonusPercentage: data.bonusPercentage,
          reason: data.reason,
          appliedAt: data.appliedAt.toDate(),
          expiresAt: data.expiresAt?.toDate()
        })
      })

      return bonuses
    } catch (error) {
      console.error('Error getting active bonuses:', error)
      return []
    }
  }

}

export const loyaltyService = new LoyaltyService()